import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Grid, Button, Switch, Divider, InputLabel, useTheme, Chip } from "@mui/material";
import { useTranslation } from "react-i18next";
import i18next from "i18next";

import { useCreateHomeSection } from "../../hooks/home_sections/useCreateHomeSection";
import { useUpdateHomeSection } from "../../hooks/home_sections/useUpdateHomeSection";
import { useSyncHomeSectionItems } from "../../hooks/home_sections/useSyncHomeSectionItems";
import FormInput from "../inputs/FormInput";
import LanguageToggle from "../global/LanguageToggle";
import { slugify } from "../../utils/slugify";
import { useGetProducts } from "../../hooks/products/useGetProducts";
import { useGetCategories } from "../../hooks/categories/useGetCategories";
import { useGetBrands } from "../../hooks/brands/useGetBrands";
import { useGetCollections } from "../../hooks/collections/useGetCollections";

const FormAddEditHomeSection = ({ currentData, isEdit = false, onSubmitSuccess }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const initialLang = localStorage.getItem("i18nextLng") || i18next.language || "ar";

    const [activeLang, setActiveLang] = useState(initialLang.startsWith("ar") ? "ar" : "en");
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    // Fields
    const [titleAr, setTitleAr] = useState("");
    const [titleEn, setTitleEn] = useState("");
    const [code, setCode] = useState("");
    const [type, setType] = useState("hero");
    const [isActive, setIsActive] = useState(true);
    const [items, setItems] = useState([]);

    const { createSectionMutate, isPending: isCreating } = useCreateHomeSection();
    const { updateSectionMutate, isUpdating } = useUpdateHomeSection();
    const { syncItemsMutate, isSyncing } = useSyncHomeSectionItems();

    const { products, isLoading: loadingProducts } = useGetProducts({ per_page: 1000 });
    const { categories, isLoading: loadingCategories } = useGetCategories({ per_page: 1000 });
    const { brands, isLoading: loadingBrands } = useGetBrands({ per_page: 1000 });
    const { collections, isLoading: loadingCollections } = useGetCollections({ per_page: 1000 });

    const createItemKey = () => `${Date.now()}_${Math.random().toString(16).slice(2)}`;

    const toEnglishValue = (val) => {
        if (val === null || val === undefined) return "";
        const str = String(val);
        return str.replace(/[\u0600-\u06FF]/g, "");
    };

    // Mapping between Section Type and Item Type
    const sectionToItemType = {
        'hero': 'hero',
        'banners': 'banner',
        'brands': 'brand',
        'products': 'product',
        'categories': 'category',
        'collections': 'collection',
        'html': 'html'
    };

    const handleSectionTypeChange = (newSectionType) => {
        setType(newSectionType);
        const newItemType = sectionToItemType[newSectionType] || 'hero';

        // Strictly update all items' item_type to match the new section type
        setItems(prev => prev.map(item => ({
            ...item,
            item_type: newItemType,
            // Clear item_id if moving to a non-reference type
            item_id: ['hero', 'banner', 'html'].includes(newItemType) ? "" : item.item_id
        })));
    };

    // Auto-generate code from English title
    useEffect(() => {
        if (titleEn && !isEdit) {
            setCode(slugify(toEnglishValue(titleEn)));
        }
    }, [titleEn, isEdit]);

    useEffect(() => {
        if (isEdit && currentData) {
            setTitleAr(currentData.title?.ar || "");
            setTitleEn(currentData.title?.en || "");
            setCode(currentData.code || "");
            setType(currentData.type || "hero");
            setIsActive(currentData.is_active ?? true);

            const apiItems = Array.isArray(currentData.items) ? currentData.items : [];
            setItems(
                apiItems.map((it, idx) => ({
                    _key: createItemKey(),
                    id: it.id,  // Preserve id from API for updates
                    item_type: it.item_type || "hero",
                    item_id: it.item_id ?? "",
                    title: {
                        en: it?.title?.en || "",
                        ar: it?.title?.ar || "",
                    },
                    subtitle: {
                        en: it?.subtitle?.en || "",
                        ar: it?.subtitle?.ar || "",
                    },
                    image: it?.image || null,
                    mobile_image: it?.mobile_image || null,
                    content: it?.content || "",
                    link_url: it?.link_url || "",
                    sort_order: it?.sort_order ?? idx,
                }))
            );
        }
    }, [isEdit, currentData]);

    const handleAddItem = () => {
        const defaultItemType = sectionToItemType[type] || 'hero';
        setItems(prev => ([
            ...prev,
            {
                _key: createItemKey(),
                item_type: defaultItemType,
                item_id: "",
                title: { en: "", ar: "" },
                subtitle: { en: "", ar: "" },
                image: null,
                mobile_image: null,
                content: "",
                link_url: "",
                sort_order: prev.length,
            }
        ]));
    };

    const handleRemoveItem = (index) => {
        setItems(prev => prev.filter((_, i) => i !== index).map((it, idx) => ({ ...it, sort_order: idx })));
    };

    const handleMoveItem = (fromIndex, toIndex) => {
        setItems(prev => {
            if (toIndex < 0 || toIndex >= prev.length) return prev;
            const next = [...prev];
            const [moved] = next.splice(fromIndex, 1);
            next.splice(toIndex, 0, moved);
            return next.map((it, idx) => ({ ...it, sort_order: idx }));
        });
    };

    const handleItemChange = (index, patch) => {
        setItems(prev => prev.map((it, i) => (i === index ? { ...it, ...patch } : it)));
    };

    const handleItemFileChange = (index, field) => (e) => {
        const nextValue = e?.target ? e.target.value : e;
        handleItemChange(index, { [field]: nextValue });
    };

    const normalizeItemsForSync = (rawItems) => {
        return (rawItems || []).map((it, idx) => {
            const normalized = {
                item_type: it.item_type,
                item_id: it.item_id ? Number(it.item_id) : null,
                title: {
                    en: it?.title?.en || "",
                    ar: it?.title?.ar || "",
                },
                subtitle: {
                    en: it?.subtitle?.en || "",
                    ar: it?.subtitle?.ar || "",
                },
                image: it.image,
                mobile_image: it.mobile_image,
                content: it.content || null,
                link_url: it.link_url || null,
                sort_order: it.sort_order ?? idx,
            };

            // Include id if exists (for updates)
            if (it.id) {
                normalized.id = it.id;
            }

            return normalized;
        });
    };

    const SubmitForm = (e) => {
        e.preventDefault();

        const payload = {
            title: {
                en: titleEn || titleAr,
                ar: titleAr || titleEn
            },
            code,
            type,
            is_active: isActive
        };

        const normalizedItems = normalizeItemsForSync(items);
        const shouldSyncItems = normalizedItems.length > 0;

        if (isEdit) {
            updateSectionMutate(
                { id: currentData.id, data: payload },
                {
                    onSuccess: () => {
                        if (!shouldSyncItems) {
                            if (onSubmitSuccess) onSubmitSuccess();
                            return;
                        }
                        syncItemsMutate(
                            { id: currentData.id, items: normalizedItems },
                            { onSuccess: () => { if (onSubmitSuccess) onSubmitSuccess(); } }
                        );
                    }
                }
            );
        } else {
            createSectionMutate(payload, {
                onSuccess: (res) => {
                    const createdId = res?.data?.id;
                    if (!createdId || !shouldSyncItems) {
                        if (onSubmitSuccess) onSubmitSuccess();
                        return;
                    }
                    syncItemsMutate(
                        { id: createdId, items: normalizedItems },
                        { onSuccess: () => { if (onSubmitSuccess) onSubmitSuccess(); } }
                    );
                }
            });
        }
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, md: 4 },
                mt: 2,
                direction: isArabic ? "rtl" : "ltr",
                backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "#FFFFFF",
                borderRadius: 4,
                border: `1px solid ${theme.palette.divider}`,
            }}
        >
            <Box component="form" onSubmit={SubmitForm} sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 2 }}>
                    <Box>
                        <Typography variant="h5" fontWeight="bold" color="primary">
                            {isEdit
                                ? (isArabic ? "تعديل قسم الصفحة الرئيسية" : "Edit Home Section")
                                : (isArabic ? "إضافة قسم جديد" : "Add New Section")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {isArabic ? "قم بتعبئة البيانات الأساسية للقسم" : "Fill in the basic info for the section"}
                        </Typography>
                    </Box>
                    <LanguageToggle activeLang={activeLang} onChange={(lng) => setActiveLang(lng)} showLabel={true} />
                </Box>

                <Grid container spacing={4}>
                    {/* Basic Info Header */}
                    <Grid item xs={12}>
                        <Typography variant="overline" color="text.secondary" fontWeight="bold">
                            {isArabic ? "البيانات الأساسية" : "Basic Information"}
                        </Typography>
                        <Divider sx={{ mb: 3, mt: 1 }} />
                    </Grid>

                    {/* Title */}
                    <Grid item xs={12} md={6}>
                        {activeLang === "ar" ? (
                            <FormInput
                                label="العنوان (عربي)"
                                placeholder="مثلاً: السلايدر الرئيسي"
                                type="text"
                                value={titleAr}
                                onChange={(e) => setTitleAr(e.target.value)}
                                required
                            />
                        ) : (
                            <FormInput
                                label="Title (English)"
                                placeholder="e.g., Main Hero Slider"
                                type="text"
                                value={titleEn}
                                onChange={(e) => setTitleEn(e.target.value)}
                                required
                            />
                        )}
                    </Grid>

                    {/* Code */}
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "الكود التعريفي" : "Code"}
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            required
                            helperText={isArabic ? "يجب أن يكون فريداً (مثال: hero-slider)" : "Must be unique (e.g., hero-slider)"}
                            disabled={isEdit}
                        />
                    </Grid>

                    {/* Type */}
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "نوع القسم" : "Section Type"}
                            type="select"
                            name="type"
                            value={type}
                            onChange={(e) => handleSectionTypeChange(e.target.value)}
                            options={[
                                { id: "hero", name: { ar: "يدوي (Hero)", en: "Hero Banner" } },
                                { id: "banners", name: { ar: "بانرات ترويجية", en: "Promotional Banners" } },
                                { id: "brands", name: { ar: "العلامات التجارية", en: "Brands" } },
                                { id: "products", name: { ar: "المنتجات", en: "Products" } },
                                { id: "categories", name: { ar: "الفئات", en: "Categories" } },
                                { id: "collections", name: { ar: "المجموعات", en: "Collections" } },
                                { id: "html", name: { ar: "محتوى HTML", en: "HTML Content" } },
                            ]}
                            required
                        />
                    </Grid>

                    {/* Status Switch */}
                    <Grid item xs={12} md={6}>
                        <InputLabel
                            sx={{
                                my: 1,
                                fontWeight: 'bold',
                                textAlign: isArabic ? 'right' : 'left',
                                direction: isArabic ? 'rtl' : 'ltr',
                            }}
                        >
                            {isArabic ? "الحالة" : "Status"}
                        </InputLabel>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: 2.5,
                            py: 1.5,
                            border: '1px solid',
                            borderColor: theme => theme.palette.divider,
                            borderRadius: '12px',
                            backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.01)',
                            transition: 'all 0.3s ease',
                        }}>
                            <Box>
                                <Typography variant="subtitle2" fontWeight="bold">
                                    {isActive ? (isArabic ? "نشط" : "Active") : (isArabic ? "غير نشط" : "Inactive")}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {isActive ? (isArabic ? "هذا القسم سيظهر للعملاء" : "Visible to customers") : (isArabic ? "هذا القسم مخفي حالياً" : "Hidden from customers")}
                                </Typography>
                            </Box>
                            <Switch
                                checked={isActive}
                                onChange={(e) => setIsActive(e.target.checked)}
                                color="primary"
                            />
                        </Box>
                    </Grid>
                </Grid>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Box>
                        <Typography variant="h6" fontWeight="bold">
                            {isArabic ? "عناصر القسم" : "Section Items"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {isArabic ? "أضف عناصر القسم وحدد ترتيبها" : "Add section items and set their order"}
                        </Typography>
                    </Box>
                    <Button variant="outlined" onClick={handleAddItem}>
                        {isArabic ? "إضافة عنصر" : "Add Item"}
                    </Button>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {items.map((item, index) => {
                        const showItemId = ['product', 'brand', 'category', 'collection'].includes(item.item_type);

                        return (
                            <Paper
                                key={item._key || index}
                                elevation={0}
                                sx={{
                                    p: 2,
                                    borderRadius: 3,
                                    border: `1px solid ${theme.palette.divider}`,
                                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)'
                                }}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Chip
                                            label={item.item_type.toUpperCase()}
                                            size="small"
                                            color="primary"
                                        />
                                        <Typography variant="subtitle1" fontWeight="bold">
                                            {isArabic ? `عنصر #${index + 1}` : `Item #${index + 1}`}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        <Button variant="text" onClick={() => handleMoveItem(index, index - 1)} disabled={index === 0}>
                                            {isArabic ? "أعلى" : "Up"}
                                        </Button>
                                        <Button variant="text" onClick={() => handleMoveItem(index, index + 1)} disabled={index === items.length - 1}>
                                            {isArabic ? "أسفل" : "Down"}
                                        </Button>
                                        <Button variant="text" color="error" onClick={() => handleRemoveItem(index)}>
                                            {isArabic ? "حذف" : "Remove"}
                                        </Button>
                                    </Box>
                                </Box>

                                <Grid container spacing={3} sx={{ mt: 0.5 }}>
                                    {showItemId && (
                                        <Grid item xs={12} md={9}>
                                            <FormInput
                                                label={
                                                    item.item_type === 'product' ? (isArabic ? "اختر المنتج" : "Select Product") :
                                                        item.item_type === 'brand' ? (isArabic ? "اختر البراند" : "Select Brand") :
                                                            item.item_type === 'category' ? (isArabic ? "اختر الفئة" : "Select Category") :
                                                                item.item_type === 'collection' ? (isArabic ? "اختر المجموعة" : "Select Collection") :
                                                                    (isArabic ? "العنصر المرتبط" : "Linked Item")
                                                }
                                                type="select"
                                                name={`item_id_${index}`}
                                                value={item.item_id}
                                                onChange={(e) => handleItemChange(index, { item_id: e.target.value })}
                                                options={
                                                    item.item_type === 'product' ? (products || []).map(p => ({ id: p.id, name: p.name })) :
                                                        item.item_type === 'brand' ? (brands || []).map(b => ({ id: b.id, name: b.name })) :
                                                            item.item_type === 'category' ? (categories || []).map(c => ({ id: c.id, name: c.name })) :
                                                                item.item_type === 'collection' ? (collections || []).map(c => ({ id: c.id, name: c.title })) :
                                                                    []
                                                }
                                                disabled={
                                                    item.item_type === 'product' ? loadingProducts :
                                                        item.item_type === 'brand' ? loadingBrands :
                                                            item.item_type === 'category' ? loadingCategories :
                                                                item.item_type === 'collection' ? loadingCollections :
                                                                    false
                                                }
                                                required={true}
                                            />
                                        </Grid>
                                    )}

                                    <Grid item xs={12} md={showItemId ? 3 : 12}>
                                        <FormInput
                                            label={isArabic ? "الترتيب" : "Sort Order"}
                                            type="number"
                                            value={item.sort_order}
                                            onChange={(e) => handleItemChange(index, { sort_order: e.target.value })}
                                            required={false}
                                        />
                                    </Grid>

                                    {/* Standalone Fields (Title, Subtitle, Link) - Only for non-reference types (except HTML maybe?) */}
                                    {!showItemId && item.item_type !== 'html' && (
                                        <>
                                            <Grid item xs={12} md={6}>
                                                {activeLang === "ar" ? (
                                                    <FormInput
                                                        label={isArabic ? "العنوان (عربي)" : "Title (AR)"}
                                                        type="text"
                                                        value={item.title?.ar || ""}
                                                        onChange={(e) => handleItemChange(index, { title: { ...item.title, ar: e.target.value } })}
                                                        required={false}
                                                    />
                                                ) : (
                                                    <FormInput
                                                        label={isArabic ? "العنوان (إنجليزي)" : "Title (EN)"}
                                                        type="text"
                                                        value={item.title?.en || ""}
                                                        onChange={(e) => handleItemChange(index, { title: { ...item.title, en: e.target.value } })}
                                                        required={false}
                                                    />
                                                )}
                                            </Grid>

                                            <Grid item xs={12} md={6}>
                                                {activeLang === "ar" ? (
                                                    <FormInput
                                                        label={isArabic ? "العنوان الفرعي (عربي)" : "Subtitle (AR)"}
                                                        type="text"
                                                        value={item.subtitle?.ar || ""}
                                                        onChange={(e) => handleItemChange(index, { subtitle: { ...item.subtitle, ar: e.target.value } })}
                                                        required={false}
                                                    />
                                                ) : (
                                                    <FormInput
                                                        label={isArabic ? "العنوان الفرعي (إنجليزي)" : "Subtitle (EN)"}
                                                        type="text"
                                                        value={item.subtitle?.en || ""}
                                                        onChange={(e) => handleItemChange(index, { subtitle: { ...item.subtitle, en: e.target.value } })}
                                                        required={false}
                                                    />
                                                )}
                                            </Grid>

                                            <Grid item xs={12} md={12}>
                                                <FormInput
                                                    label={isArabic ? "الرابط" : "Link URL"}
                                                    type="text"
                                                    value={item.link_url}
                                                    onChange={(e) => handleItemChange(index, { link_url: e.target.value })}
                                                    required={false}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                                <FormInput
                                                    type="file"
                                                    name={`image_${index}`}
                                                    labelImage={isArabic ? "صورة" : "Image"}
                                                    value={item.image}
                                                    onChange={handleItemFileChange(index, 'image')}
                                                    width={160}
                                                    height={110}
                                                    borderRadius={12}
                                                    required={false}
                                                />
                                            </Grid>

                                            <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                                <FormInput
                                                    type="file"
                                                    name={`mobile_image_${index}`}
                                                    labelImage={isArabic ? "صورة الموبايل" : "Mobile Image"}
                                                    value={item.mobile_image}
                                                    onChange={handleItemFileChange(index, 'mobile_image')}
                                                    width={160}
                                                    height={110}
                                                    borderRadius={12}
                                                    required={false}
                                                />
                                            </Grid>
                                        </>
                                    )}

                                    {/* HTML Content Field */}
                                    {item.item_type === 'html' && (
                                        <Grid item xs={12}>
                                            <FormInput
                                                label={isArabic ? "محتوى HTML" : "HTML Content"}
                                                type="editor"
                                                value={item.content}
                                                onChange={(e) => handleItemChange(index, { content: e.target.value })}
                                                placeholder={isArabic ? "اكتب كود HTML أو المحتوى هنا..." : "Enter HTML code or content here..."}
                                            />
                                        </Grid>
                                    )}
                                </Grid>
                            </Paper>
                        );
                    })}
                </Box>

                <Divider sx={{ my: 1 }} />

                <Box display="flex" justifyContent="flex-end">
                    <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        disabled={isCreating || isUpdating || isSyncing}
                        sx={{
                            minWidth: 160,
                            borderRadius: '12px',
                            fontWeight: 'bold',
                            textTransform: 'none',
                            boxShadow: theme.shadows[4],
                        }}
                    >
                        {isCreating || isUpdating || isSyncing ? (isArabic ? "جاري الحفظ..." : "Saving...") : (isEdit ? (isArabic ? "تحديث البيانات" : "Update Data") : (isArabic ? "إنشاء القسم" : "Create Section"))}
                    </Button>
                </Box>
            </Box>
        </Paper>
    );
};

export default FormAddEditHomeSection;
